import { useRouter } from "next/router"
import { type FC, type ReactNode, useContext, useEffect } from "react"

import { UserContext } from "../providers/UserProvider"
import { isCheckoutPage } from "../utils/routing"
import { useCommunityCustomization } from "../utils/useCommunityCustomization"
import { useWindowSize } from "../utils/useWindowSize"
import BackgroundMedia from "./BackgroundMedia"
import { CommunityMemberGateModal } from "./CommunityMembershipGate"
import { ContentWrappingMenu } from "./ContentWrappingMenu"
import WERZLogoIcon from "./icons/WERZLogoIcon"

const AppNavigation: FC<{
  children: ReactNode | ReactNode[]
}> = ({ children }) => {
  const { user, communities, isInitialized } = useContext(UserContext)
  const router = useRouter()
  const { isDesktop } = useWindowSize()

  const customizationData = useCommunityCustomization()
  if (!customizationData) return null
  const { styles: communityStyles, code: communityCode } = customizationData

  const isEventStatsPage = router.pathname === "/c/[code]/event/[id]/sales-stats"
  const isEventPage =
    router.pathname === "/c/[code]/event/[id]" || router.pathname === "/c/[code]/event/[id]/tickets"
  const isPublicPage =
    isEventStatsPage ||
    (isEventPage && customizationData.publicEventPages !== false) ||
    router.pathname === "/c/[code]/shop/[productIdOrSlug]" ||
    router.pathname === "/c/[code]/download" ||
    router.pathname === "/p/[code]/competition"
  const isCheckoutRedirect =
    router.pathname === "/c/[code]/checkout-return" && router.query.purchaseIds
  const isClaimCardPage = router.pathname === "/c/[code]/claim-card"
  const isCheckout = isCheckoutPage(router.asPath)
  const isCommunityMember = isInitialized && !!communities.find((c) => c.code === communityCode)
  const showContent = !!(
    isCheckoutRedirect ||
    isPublicPage ||
    isClaimCardPage ||
    isCheckout ||
    !!(user && isCommunityMember && !customizationData.styles.global.mobileOnly)
  )

  const bgSource =
    communityStyles.global.nonMemberBg && !showContent
      ? communityStyles.global.nonMemberBg
      : isDesktop
        ? communityStyles.global.bgDesktop
        : communityStyles.global.bgMobile

  const bgColor =
    communityStyles.global.nonMemberBgColor && !showContent
      ? communityStyles.global.nonMemberBgColor
      : communityStyles.global.bgColor

  const showLogo = !showContent
  const pageBackground = isCheckoutPage(router.asPath) ? "#FFFFFF" : bgColor
  useEffect(() => {
    const root = document.documentElement
    const body = document.body
    const previousRootBackground = root.style.backgroundColor
    const previousBodyBackground = body.style.backgroundColor

    root.style.backgroundColor = pageBackground
    body.style.backgroundColor = pageBackground

    return () => {
      root.style.backgroundColor = previousRootBackground
      body.style.backgroundColor = previousBodyBackground
    }
  }, [pageBackground])

  return (
    <div
      className={`flex flex-col w-full flex-grow z-20 ${
        isCheckoutPage(router.asPath) ? "font-mono" : communityStyles.global.mainFont
      }`}
      style={{
        background: pageBackground,
      }}
    >
      {!isCheckoutPage(router.asPath) && bgSource ? (
        <BackgroundMedia
          bgColor={bgColor}
          bgSource={bgSource}
          className={!showContent ? (communityStyles.global.nonMemberBgContainerStyles ?? "") : ""}
          mediaClassName={!showContent ? communityStyles.global.nonMemberBgStyles : undefined}
          showContentMargin={showContent}
        />
      ) : null}
      {showLogo ? (
        <div className="z-[20] fixed top-6 left-6" onClick={() => void router.push("/")}>
          <WERZLogoIcon
            color={communityStyles.global.werzLogoColor ?? "#FFFFFF"}
            height={15}
            width={56}
          />
        </div>
      ) : null}
      <ContentWrappingMenu showContent={!!showContent}>{children}</ContentWrappingMenu>
      <CommunityMemberGateModal isOpen={isInitialized && !showContent} />
    </div>
  )
}

export default AppNavigation
